import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import Styles from './OverviewCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import {
  ChartComponent,
  OverviewInfoCard,
  IFText,
  IFSkeleton,
} from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { useTranslation } from 'react-i18next'
import {
  getEnergyUnit,
  formatXAxisDate,
  getSeriesLabelsByPeriod,
} from 'Utils/Calculations'

const OverviewCard = ({
  overview,
  fetchOverview,
  fetchOverviewRequestState,
  filter,
}) => {
  const { t } = useTranslation()
  const selectedPeriod = filter[0]?.value
  useEffect(() => {
    fetchOverview(filter)
  }, [filter])

  const { current: currentLabel, previous: previousLabel } =
    getSeriesLabelsByPeriod(selectedPeriod)
  const series = [
    {
      name: currentLabel,
      type: 'area',
      data: overview.data?.graph.current?.map((item) => item.energy) || [],
    },
    {
      name: previousLabel,
      type: 'line',
      data: overview.data?.graph.previous?.map((item) => item.energy) || [],
    },
  ]

  const allValues = series.flatMap((s) => s.data)
  const maxValue = Math.max(...allValues)
  const getLabelIndicesByPeriod = (dataArray, period) => {
    if (!dataArray || dataArray.length === 0) return []

    const indices = []
    const length = dataArray.length
    const pushIndex = (i) => {
      if (!indices.includes(i)) indices.push(i)
    }

    if (
      period === 'thisMonth' ||
      period === 'lastMonth' ||
      period === 'last3Months'
    ) {
      for (let i = 0; i < length; i += 7) {
        pushIndex(i)
      }
      return indices
    }

    if (period === 'last6Months') {
      dataArray.forEach((item, index) => {
        const date = new Date(item.date)
        const day = date.getDate()

        if (day === 1 || day === 15) {
          pushIndex(index)
        }
      })
      return indices
    }
    dataArray.forEach((item, index) => {
      const date = new Date(item.date)
      if (date.getDate() === 1) {
        pushIndex(index)
      }
    })
    return indices
  }
  const monthStartIndices =
    overview.data?.graph?.current?.length == 0
      ? getLabelIndicesByPeriod(overview.data?.graph.previous, selectedPeriod)
      : getLabelIndicesByPeriod(overview.data?.graph.current, selectedPeriod)

  const { divisor, unit } = getEnergyUnit(maxValue)
  const options = {
    chart: {
      id: 'energy-consumption',
      toolbar: { show: false },
    },
    colors: [Colors.primary, Colors.Analytics.Icons],
    stroke: {
      curve: 'smooth',
      width: [3, 2],
      dashArray: [0, 4],
    },
    fill: {
      type: ['gradient', 'none'],
      gradient: {
        shade: 'light',
        shadeIntensity: 0.2,
        gradientToColors: [Colors.Analytics.GraphGradient],
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100],
        inverseColors: false,
        type: 'vertical',
      },
    },
    xaxis: {
      categories:
        overview.data?.graph.current?.length > 0
          ? overview.data?.graph.current?.map((item, index) => {
              const label = formatXAxisDate(item.date, selectedPeriod)
              return monthStartIndices.includes(index) ? label : ''
            }) || []
          : overview.data?.graph.previous?.map((item, index) => {
              const label = formatXAxisDate(item.date, selectedPeriod)
              return monthStartIndices.includes(index) ? label : ''
            }) || [],
      labels: {
        rotate: -45,
        hideOverlappingLabels: false,
      },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (value) => `${Math.ceil(value / divisor)} ${unit}`,
      },
    },
    tooltip: {
      x: {
        formatter: (_, { dataPointIndex }) => {
          const item =
            overview.data?.graph.current?.[dataPointIndex] ||
            overview.data?.graph.previous?.[dataPointIndex]
          return item?.date ? formatXAxisDate(item.date, selectedPeriod) : ''
        },
      },
    },
    dataLabels: { enabled: false },
    grid: { show: false },
  }
  const currentData = Array.isArray(overview.data?.graph?.current)
    ? overview.data.graph.current
    : []

  const hasNoCurrentData =
    currentData.length === 0 ||
    currentData.every((item) => Number(item.energy) === 0)

  return (
    <div
      style={{
        backgroundColor: Colors.white,
      }}
      className={Styles.Card}
    >
      <OverviewInfoCard
        chargingSessions={overview.data?.stats.sessions.value}
        chargingSessionsPercentage={overview.data?.stats.sessions.change}
        energyConsumed={overview.data?.stats.consumed.value}
        energyConsumedPercentage={overview.data?.stats.consumed.change}
        averageConsumption={overview.data?.stats.avgConsumption.value}
        averageConsumptionPercentage={
          overview.data?.stats.avgConsumption.change
        }
        averageDuration={overview.data?.stats.avgDuration.value}
        averageDurationPercentage={overview.data?.stats.avgDuration.change}
        isLoading={fetchOverviewRequestState === RequestState.LOADING}
      />

      <div className={Styles.GraphContainer}>
        {fetchOverviewRequestState === RequestState.LOADING ? (
          <div className={Styles.ChartSkeleton}>
            <IFSkeleton width={'100%'} height={'100%'} />
          </div>
        ) : (
          <>
            <ChartComponent
              options={options}
              series={series}
              height="100%"
              width="100%"
              type="area"
              className={
                (series.length === 0 ||
                  series.every(
                    (s) =>
                      !s.data ||
                      s.data.length === 0 ||
                      s.data.every((val) => Number(val) === 0),
                  ) ||
                  hasNoCurrentData) &&
                Styles.OverlayTest
              }
            />
            {(series.length === 0 ||
              series.every(
                (s) =>
                  !s.data ||
                  s.data.length === 0 ||
                  s.data.every((val) => Number(val) === 0),
              ) ||
              hasNoCurrentData) && (
              <IFText
                className={Styles.OverlayText}
                style={{ color: Colors.red }}
              >
                {t('ConnectorUtilization.NoData')}
              </IFText>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  overview: AnalyticsSelectors.getOverview(state),
  fetchOverviewRequestState:
    AnalyticsSelectors.getFetchOverviewRequestState(state),
})
function mapDispatchToProps(dispatch) {
  return {
    fetchOverview: (filter) => dispatch(AnalyticsActions.fetchOverview(filter)),
  }
}
OverviewCard.propTypes = {
  filter: PropTypes.object,
  fetchOverview: PropTypes.func,
  fetchOverviewRequestState: PropTypes.number,
  overview: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(OverviewCard)
